import React, { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Clock, IndianRupee, X } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fmtDate } from "@/lib/api";

const TYPES = [
  { key: "all", label: "All" },
  { key: "entry_gift", label: "Entry Gift" },
  { key: "first_premium_cashback", label: "First Premium" },
  { key: "lucky_draw", label: "Lucky Draw" },
  { key: "agent_share", label: "Agent Share" },
];

const PRETTY_TYPE = {
  entry_gift: { label: "Entry Gift", color: "bg-rose-50 text-rose-700" },
  first_premium_cashback: { label: "First Premium Cashback", color: "bg-emerald-50 text-emerald-700" },
  lucky_draw: { label: "Lucky Draw Winner", color: "bg-amber-50 text-amber-700" },
  agent_share: { label: "Agent Share", color: "bg-violet-50 text-violet-700" },
};

export default function BenefitApprovals() {
  const [list, setList] = useState([]);
  const [typeTab, setTypeTab] = useState("all");
  const [statusTab, setStatusTab] = useState("pending");
  const [editing, setEditing] = useState(null);  // { item, amount, note }

  const load = () => api.get(`/admin/pending-benefits?type=${typeTab}&status=${statusTab}`).then((r) => setList(r.data));
  useEffect(() => { load(); }, [typeTab, statusTab]);

  const decide = async (item, decision, amountOverride, note) => {
    try {
      await api.post(`/admin/pending-benefits/${item.id}/decision`, {
        decision, amount: amountOverride ? parseFloat(amountOverride) : undefined,
        note: note || "",
      });
      toast.success(`Benefit ${decision}d`);
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="benefit-approvals-page">
      <h1 className="text-3xl font-extrabold tracking-tight">Benefit Approvals</h1>
      <p className="text-sm text-slate-500">Review and credit benefits to customer wallets</p>

      <div className="mt-4 flex gap-2 flex-wrap">
        {TYPES.map((t) => (
          <button key={t.key} data-testid={`type-tab-${t.key}`} onClick={() => setTypeTab(t.key)} className={`px-3 py-1.5 rounded-xl text-xs font-bold ${typeTab === t.key ? "bg-tenant-primary text-white" : "bg-white border border-slate-200"}`}>{t.label}</button>
        ))}
      </div>

      <div className="mt-3 flex gap-2">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <button key={s} data-testid={`status-tab-${s}`} onClick={() => setStatusTab(s)} className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider ${statusTab === s ? "bg-slate-900 text-white" : "bg-white border border-slate-200"}`}>{s}</button>
        ))}
      </div>

      <div className="mt-4 bg-white border border-slate-100 rounded-2xl card-shadow table-scroll">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
            <tr>
              <th className="text-left p-3">Customer</th>
              <th className="text-left p-3">Type</th>
              <th className="text-right p-3">Amount</th>
              <th className="text-center p-3">Status</th>
              <th className="text-right p-3">Created</th>
              <th className="text-right p-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.map((it) => {
              const t = PRETTY_TYPE[it.type] || { label: it.type, color: "bg-slate-100" };
              return (
                <tr key={it.id} data-testid={`pb-row-${it.id}`} className="hover:bg-slate-50">
                  <td className="p-3">
                    <div className="font-bold">{it.customer_name}</div>
                    <div className="text-[10px] text-slate-400">+91 {it.customer_phone}</div>
                  </td>
                  <td className="p-3">
                    <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded-full ${t.color}`}>{t.label}</span>
                  </td>
                  <td className="p-3 text-right font-extrabold">{fmtINR(it.amount)}</td>
                  <td className="p-3 text-center"><Status status={it.status} /></td>
                  <td className="p-3 text-right text-xs text-slate-500">{fmtDate(it.created_at)}</td>
                  <td className="p-3 text-right">
                    {it.status === "pending" ? (
                      <div className="flex gap-1.5 justify-end">
                        <button data-testid={`open-decide-${it.id}`} onClick={() => setEditing({ item: it, amount: it.amount, note: "" })} className="text-[11px] bg-emerald-50 text-emerald-700 font-bold px-2.5 py-1.5 rounded-lg">Review</button>
                        <button data-testid={`quick-reject-${it.id}`} onClick={() => decide(it, "reject", null, "Quick reject")} className="text-[11px] bg-rose-50 text-rose-600 font-bold px-2.5 py-1.5 rounded-lg">Reject</button>
                      </div>
                    ) : (
                      <span className="text-[11px] text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {list.length === 0 && <tr><td colSpan={6} className="text-center p-6 text-slate-400">No items.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur grid place-items-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-3xl p-5 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-extrabold text-lg">Approve Benefit</div>
              <button onClick={() => setEditing(null)}><X className="w-4 h-4" /></button>
            </div>
            <div className="text-sm text-slate-600 mt-2">For: <b>{editing.item.customer_name}</b></div>
            <div className="text-xs text-slate-500">Type: {PRETTY_TYPE[editing.item.type]?.label}</div>

            <label className="mt-4 block text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Amount to Credit</label>
            <div className="mt-1.5 flex items-center gap-2 border-2 border-slate-200 rounded-2xl px-3 py-2.5">
              <IndianRupee className="w-4 h-4 text-slate-400" />
              <input data-testid="edit-amount" value={editing.amount} onChange={(e) => setEditing({ ...editing, amount: e.target.value.replace(/[^\d.]/g, "") })} className="flex-1 outline-none font-extrabold" />
            </div>

            <label className="mt-3 block text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Note (optional)</label>
            <input data-testid="edit-note" value={editing.note} onChange={(e) => setEditing({ ...editing, note: e.target.value })} className="mt-1.5 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none" />

            <div className="mt-4 grid grid-cols-2 gap-2">
              <button data-testid="reject-btn" onClick={() => decide(editing.item, "reject", null, editing.note)} className="py-3 rounded-xl bg-rose-50 text-rose-700 font-bold">Reject</button>
              <button data-testid="approve-btn" onClick={() => decide(editing.item, "approve", editing.amount, editing.note)} className="py-3 rounded-xl bg-emerald-600 text-white font-bold">Approve & Credit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Status = ({ status }) => {
  const map = {
    pending: { c: "bg-amber-50 text-amber-700", I: Clock },
    approved: { c: "bg-emerald-50 text-emerald-700", I: CheckCircle2 },
    rejected: { c: "bg-rose-50 text-rose-700", I: XCircle },
  };
  const { c, I } = map[status] || map.pending;
  return <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${c}`}><I className="w-3 h-3" />{status}</span>;
};
